
import React, { useRef, useEffect } from 'react';
import PropTypes from "prop-types";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Typography, CircularProgress } from "@mui/material";
import Card from "@mui/material/Card";

function AudienceCountChart({
  title,
  datasets,
  previousdatasets,
  futuredatasets,
  isPredictionEnabled,
  isLoading,
  entitlements, // ✅ Added prop
  viewMode, // ✅ NEW
  onViewModeChange, // ✅ NEW
  profileColorMap // ✅ Global Color Map
}) {

  // ---------------------------------------------------------------------
  // RENDER LOADER
  // ---------------------------------------------------------------------
  const renderLoader = () => (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        zIndex: 10,
        borderRadius: "0.75rem",
      }}
    >
      <CircularProgress size={32} thickness={4} color="accent" />
    </MDBox>
  );

  const platformSet = new Set(datasets ? datasets.map((profile) => profile.platform) : []);
  let derivedTitle = '';

  if (platformSet.size === 1) {
    const platform = platformSet.values().next().value;
    derivedTitle = platform === "YouTube" ? "Subscribers Over Time" : "Followers Over Time";
  } else if (platformSet.size > 1) {
    derivedTitle = [...platformSet].includes("YouTube") ? "Subscribers Over Time" : "Followers Over Time";
  } else {
    derivedTitle = "Followers Over Time";
  }

  // -----------------------------------------------------------
  // DATA PREPARATION
  // -----------------------------------------------------------

  // Safely handle inputs
  const safeDatasets = datasets || [];
  const safePrevious = previousdatasets || [];
  const safeFuture = futuredatasets || [];

  const actualLabels = safeDatasets.length > 0 ? safeDatasets[0].data.map((item) => item.date) : [];
  const predictedLabels =
    isPredictionEnabled && safeFuture.length > 0
      ? safeFuture[0].data.slice(safeDatasets[0].data.length).map((item) => item.date)
      : [];
  const chartLabels = [...actualLabels, ...predictedLabels];

  const updatedPreviousDatasets = isPredictionEnabled
    ? [...safePrevious, ...safeDatasets]
    : safePrevious;

  // Compute Current Chart Data
  const computedChartData = {
    labels: chartLabels,
    datasets: [
      ...safeDatasets.map((profile) => {
        const isYouTube = profile.platform === "YouTube";
        const chartValues = [];
        const predictionFlags = [];

        profile.data.forEach((item) => {
          const actual = isYouTube ? item.subscribers || 0 : item.followers || 0;
          const predicted = isYouTube
            ? item.predicted_subscribers || 0
            : item.predicted_followers || 0;

          if (actual > 0) {
            chartValues.push(actual);
            predictionFlags.push(false);
          } else if (predicted > 0) {
            chartValues.push(predicted);
            predictionFlags.push(true);
          } else {
            chartValues.push(0);
            predictionFlags.push(false);
          }
        });

        return {
          label: profile.name || profile.label,
          data: chartValues,
          isPredictedFlags: predictionFlags,
          profileId: profile.profileId,
          profilePictureUrl: profile.profilePictureUrl || "",
          platform: profile.platform || "Unknown",
          metricName: isYouTube
            ? "Subscribers"
            : profile.platform === "Facebook" || profile.platform === "Instagram"
              ? "Followers"
              : "Metric",
        };
      }),
      ...(isPredictionEnabled
        ? safeFuture.map((profile) => {
          const isYouTube = profile.platform === "YouTube";
          const chartValues = [];
          const predictionFlags = [];

          profile.data.forEach((item) => {
            const predicted = isYouTube
              ? item.predicted_subscribers || item.subscribers || 0
              : item.predicted_followers || item.followers || 0;
            chartValues.push(predicted);
            predictionFlags.push(true);
          });

          return {
            label: `${profile.name || profile.label} (Predicted)`,
            data: chartValues,
            isPredictedFlags: predictionFlags,
            borderDash: [5, 5],
            profileId: profile.profileId,
            profilePictureUrl: profile.profilePictureUrl || "",
            platform: profile.platform || "Unknown",
            metricName: "Predicted Followers",
          };
        })
        : []),
    ],

    previousdatasets: updatedPreviousDatasets.map((profile) => ({
      label: profile.name || profile.label,
      data: profile.data.map((item) => {
        if (profile.platform === "YouTube") {
          return item.subscribers;
        } else if (profile.platform === "Facebook" || profile.platform === "Instagram") {
          return item.followers;
        } else {
          return 0;
        }
      }),
      profileId: profile.profileId,
      profilePictureUrl: profile.profilePictureUrl || '',
      platform: profile.platform || "Unknown",
      metricName:
        profile.platform === "YouTube"
          ? "Subscribers"
          : profile.platform === "Facebook" || profile.platform === "Instagram"
            ? "Followers"
            : "Metric",
    })),
  };

  // -----------------------------------------------------------
  // PERSISTENCE LOGIC
  // -----------------------------------------------------------
  // We use a Ref to store the last valid data to show during loading
  const lastChartDataRef = useRef(null);

  // If we have actual data, update the ref
  if (safeDatasets.length > 0) {
    lastChartDataRef.current = computedChartData;
  }

  // Determine what to render:
  // 1. If we have current data, use it.
  // 2. If we are loading and have NO current data (e.g. parent cleared it), use the cached ref.
  // 3. Otherwise (no data, not loading OR no cache), use the computed empty data.
  let dataToRender = computedChartData;
  if (isLoading && safeDatasets.length === 0 && lastChartDataRef.current) {
    dataToRender = lastChartDataRef.current;
  }

  const hasData = (dataToRender.datasets && dataToRender.datasets.length > 0);

  // -----------------------------------------------------------
  // RENDER
  // -----------------------------------------------------------

  const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        This chart shows the total number of Audience over time for a profile. It provides a
        historical view of how the follower base has grown or declined.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>How to read the chart:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        - <strong>Upward trend</strong>: Your Audience is increasing over time. <br />
        - <strong>Flat line</strong>: No significant follower change on those days. <br />
        - <strong>Downward trend</strong>: A decline in Audience over time. <br />
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Track overall follower growth. <br />
        - Identify when follower growth accelerated or slowed down. <br />
        - Compare follower trends with campaigns, content strategy, or external events. <br />
        - Evaluate the long-term impact of marketing efforts.
      </Typography>
    </div>
  );

  return (
    <div style={{ position: "relative" }}>
      {isLoading && renderLoader()}

      {hasData ? (
        <DefaultLineChart
          title={derivedTitle}
          description={tooltipContent}
          height="19.125rem"
          chart={dataToRender}
          rawData={safeDatasets}
          entitlements={entitlements} // ✅ Pass entitlements
          isSnapshot={true} // ✅ Fix: Use Snapshot aggregation (Last Value) instead of Sum
          viewMode={viewMode} // ✅ Pass down CORRECT PROP NAME
          onViewModeChange={onViewModeChange} // ✅ Pass down
          profileColorMap={profileColorMap} // ✅ Pass Global Colors
        />
      ) : (
        <Card sx={{ height: "19.125rem" }}>
          {!isLoading && (
            <MDTypography variant="body2" color="text" sx={{ p: 3, textAlign: "center" }}>
              No follower data available.
            </MDTypography>
          )}
        </Card>
      )}
    </div>
  );
}

// 🛡️ PropTypes Validation
AudienceCountChart.propTypes = {
  title: PropTypes.string.isRequired,
  datasets: PropTypes.array.isRequired,
  previousdatasets: PropTypes.array.isRequired,
  futuredatasets: PropTypes.array,
  isPredictionEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  entitlements: PropTypes.object, // ✅ Added propType
  viewMode: PropTypes.string,
  onViewModeChange: PropTypes.func,
  profileColorMap: PropTypes.object,
};

export default AudienceCountChart;